import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import { AlertCircle, CircleCheck, Key, Loader } from "lucide-react";
import type { FpMessagePart } from "../../../../../schemas/codegen";
import { ToolError } from "./shared";

interface AddSecretsProps {
  part: Extract<FpMessagePart, { type: "tool-call" }>;
}

export function AddSecrets({ part }: AddSecretsProps) {
  const secretCount = Array.isArray(part.args?.secrets)
    ? (part.args.secrets as unknown[]).length
    : 1;

  const isError = part.status === "error";
  const baseText = `${secretCount} secret${secretCount !== 1 ? "s" : ""}`;
  const displayName =
    part.status === "pending"
      ? `Adding ${baseText}`
      : part.status === "complete"
        ? `Added ${baseText}`
        : `Failed to add ${baseText}`;

  const statusIcon =
    part.status === "pending" ? (
      <Loader className="w-3 h-3 animate-spin" />
    ) : part.status === "complete" ? (
      <CircleCheck className="w-3 h-3 text-muted-foreground" />
    ) : isError ? (
      <AlertCircle className="w-3 h-3 text-warning" />
    ) : null;

  if (isError) {
    return (
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="add-secrets" className="border rounded-md">
          <AccordionTrigger className="hover:no-underline px-3 py-2 hover:bg-muted/30 transition-colors text-sm">
            <div className="flex items-center gap-2 w-full">
              <Key className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
              <div className="flex-1 text-left min-w-0">
                <span>{displayName}</span>
              </div>
              <div className="flex-shrink-0 mr-1">{statusIcon}</div>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="px-3 pb-2">
              {part.result ? <ToolError result={part.result} /> : null}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center gap-2 px-3 py-2 rounded-md border",
        "bg-muted/50 hover:bg-muted",
      )}
    >
      <Key className="w-4 h-4 flex-shrink-0 text-muted-foreground" />

      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium">{displayName}</span>
      </div>

      <div className="flex-shrink-0">{statusIcon}</div>
    </div>
  );
}
